import { create } from 'zustand';
import { 
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  or
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { compressImage } from '../utils/imageCompression';
import { StandardIngredient, CreateStandardIngredientInput, UpdateStandardIngredientInput, LanguageCode } from '../types/ingredients';
import { syncCatalogForIngredient, removeFromCatalog } from '../utils/ingredientCatalogSync';

interface StandardIngredientsStore {
  ingredients: StandardIngredient[];
  searchTerm: string;
  selectedLanguages: LanguageCode[];
  isLoading: boolean;
  error: string | null;
  setSearchTerm: (term: string) => void;
  toggleLanguage: (lang: LanguageCode) => void;
  fetchIngredients: () => Promise<void>;
  addIngredient: (input: CreateStandardIngredientInput) => Promise<string>;
  updateIngredient: (input: UpdateStandardIngredientInput) => Promise<void>;
  removeIngredient: (id: string) => Promise<void>;
  processImage: (file: File) => Promise<string>;
  /** @deprecated Utiliser fetchIngredients() à la place — évite un listener permanent qui recharge à chaque reconnexion réseau/token. */
  initializeSubscription: () => () => void;
}

// Firestore Web SDK rejette les `undefined`. On retire aussi les `NaN`
// (calories parfois stockées à NaN après d’anciens imports — cassent les
// <input type="number"> à l’édition).
function sanitizeIngredientPayload<T>(payload: T): T {
  if (Array.isArray(payload)) {
    return payload.map((item) => sanitizeIngredientPayload(item)) as unknown as T;
  }
  if (typeof payload === 'number' && Number.isNaN(payload)) {
    return undefined as unknown as T;
  }
  if (payload && typeof payload === 'object' && !(payload instanceof Date)) {
    const cleaned: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(payload as Record<string, unknown>)) {
      if (value === undefined) continue;
      if (typeof value === 'number' && Number.isNaN(value)) continue;
      cleaned[key] = sanitizeIngredientPayload(value as unknown);
    }
    return cleaned as T;
  }
  return payload;
}

function normalizeIngredientFromFirestore(
  id: string,
  data: Record<string, unknown>
): StandardIngredient {
  const rawAttrs =
    data.attributes && typeof data.attributes === 'object'
      ? (data.attributes as Record<string, unknown>)
      : {};
  const caloriesRaw = rawAttrs.calories;
  const calories =
    typeof caloriesRaw === 'number' && !Number.isNaN(caloriesRaw) ? caloriesRaw : undefined;

  return {
    id,
    ...data,
    name: (data.name as string) || '',
    language: (data.language as LanguageCode) || 'en',
    category: (data.category as StandardIngredient['category']) || 'other',
    preferredUnit: (data.preferredUnit as StandardIngredient['preferredUnit']) || 'gram',
    picture: typeof data.picture === 'string' ? data.picture : '',
    aliases: Array.isArray(data.aliases) ? (data.aliases as string[]) : [],
    attributes: {
      glutenFree: Boolean(rawAttrs.glutenFree),
      vegan: Boolean(rawAttrs.vegan),
      vegetarian: Boolean(rawAttrs.vegetarian),
      organic: Boolean(rawAttrs.organic),
      allergens: Array.isArray(rawAttrs.allergens) ? (rawAttrs.allergens as string[]) : [],
      seasonal: Array.isArray(rawAttrs.seasonal) ? (rawAttrs.seasonal as string[]) : [],
      ...(calories !== undefined ? { calories } : {}),
    },
  } as StandardIngredient;
}

export const useStandardIngredientsStore = create<StandardIngredientsStore>((set, get) => {
  return {
    ingredients: [],
    searchTerm: '',
    selectedLanguages: ['en', 'fr'],
    isLoading: false,
    error: null,

    setSearchTerm: (term: string) => {
      set({ searchTerm: term });
    },

    toggleLanguage: (lang: LanguageCode) => {
      set(state => {
        const languages = state.selectedLanguages.includes(lang)
          ? state.selectedLanguages.filter(l => l !== lang)
          : [...state.selectedLanguages, lang];
        
        // Ensure at least one language is selected
        return { 
          selectedLanguages: languages.length > 0 ? languages : state.selectedLanguages 
        };
      });
    },

    addIngredient: async (input: CreateStandardIngredientInput) => {
      try {
        const now = new Date();
        const ingredientData = sanitizeIngredientPayload({
          ...input,
          createdAt: now,
          updatedAt: now
        });

        const docRef = await addDoc(collection(db, 'standardIngredients'), ingredientData);

        // Dual-write: best-effort sync into ingredientCatalogs/{lang}. The
        // helper skips pending/unsupported entries internally, so creating a
        // pending ingredient correctly results in a no-op here.
        await syncCatalogForIngredient(docRef.id, {
          ...(ingredientData as Partial<StandardIngredient>),
          id: docRef.id,
        });
        return docRef.id;
      } catch (error) {
        console.error('Error adding ingredient:', error);
        throw new Error('Failed to add ingredient');
      }
    },

    updateIngredient: async (input: UpdateStandardIngredientInput) => {
      try {
        const { id, ...updateData } = input;
        const ingredientRef = doc(db, 'standardIngredients', id);

        // Read previous state BEFORE the write so we can detect a language
        // change and remove the stale entry from the old catalog doc.
        const prevSnap = await getDoc(ingredientRef);
        const previousLanguage = prevSnap.exists()
          ? (prevSnap.data()?.language as string | undefined)
          : undefined;

        const sanitizedUpdate = sanitizeIngredientPayload({
          ...updateData,
          updatedAt: new Date(),
        });

        await updateDoc(ingredientRef, sanitizedUpdate);

        // Compose the post-update view for the catalog sync. The store
        // always passes the full edited ingredient on save, so this merge
        // is equivalent to re-reading the doc but avoids an extra round-trip.
        const afterView = {
          ...(prevSnap.exists() ? (prevSnap.data() as Partial<StandardIngredient>) : {}),
          ...(sanitizedUpdate as Partial<StandardIngredient>),
          id,
        };
        await syncCatalogForIngredient(id, afterView, { previousLanguage });
      } catch (error) {
        console.error('Error updating ingredient:', error);
        throw new Error('Failed to update ingredient');
      }
    },

    removeIngredient: async (id: string) => {
      try {
        const ingredientRef = doc(db, 'standardIngredients', id);

        // Capture language before delete so we can target the right catalog
        // doc; fall back to fan-out across all supported langs if missing.
        const prevSnap = await getDoc(ingredientRef);
        const language = prevSnap.exists()
          ? (prevSnap.data()?.language as string | undefined)
          : undefined;

        await deleteDoc(ingredientRef);
        await removeFromCatalog(id, language);
      } catch (error) {
        console.error('Error removing ingredient:', error);
        throw new Error('Failed to remove ingredient');
      }
    },

    processImage: async (file: File) => {
      try {
        const compressedFile = await compressImage(file);
        
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(compressedFile);
        });
      } catch (error) {
        console.error('Error processing image:', error);
        throw error;
      }
    },

    fetchIngredients: async () => {
      const { selectedLanguages } = get();
      set({ isLoading: true, error: null });
      try {
        const languageConditions = selectedLanguages.map(lang =>
          where('language', '==', lang)
        );
        const ingredientsQuery = query(
          collection(db, 'standardIngredients'),
          or(...languageConditions),
          orderBy('name', 'asc')
        );
        const snapshot = await getDocs(ingredientsQuery);
        const ingredients: StandardIngredient[] = [];
        snapshot.forEach((docSnap) => {
          ingredients.push(
            normalizeIngredientFromFirestore(docSnap.id, docSnap.data() as Record<string, unknown>)
          );
        });
        set({ ingredients, error: null });
      } catch (error) {
        console.error('Error fetching ingredients:', error);
        set({ error: (error as Error).message });
      } finally {
        set({ isLoading: false });
      }
    },

    initializeSubscription: () => {
      // Kept for backwards compatibility — delegates to a one-shot fetch so no
      // persistent listener is registered (avoids re-reads on every network
      // reconnection, token refresh or screen wake).
      get().fetchIngredients();
      return () => {};
    },
  };
});